/**
 * 最小限の画像コンポーネント - デバッグ用
 */

import React, { useState, useEffect } from 'react';
import { Spot } from '../types/core';
import { shouldTryGooglePlacesImage } from '../utils/spot-image-validator';

export interface SpotImageMinimalProps {
    spot: Spot;
    className?: string;
}

export const SpotImageMinimal: React.FC<SpotImageMinimalProps> = ({
    spot,
    className = ''
}) => {
    const [googlePlacesImageUrl, setGooglePlacesImageUrl] = useState<string | null>(null);
    const [isLoadingGoogleImage, setIsLoadingGoogleImage] = useState(false);

    // 基本画像URLを取得
    const getImageUrl = (): string | null => {
        const sources = [
            spot.image?.url,
            (spot as any).photo?.pc?.l,
            (spot as any).photo_url,
            (spot as any).Image
        ];

        const validUrl = sources.find(url => url && typeof url === 'string' && url.startsWith('http'));

        if (validUrl && validUrl.includes('hotp.jp')) {
            return `https://flaato-api.flaato-app.workers.dev/api/proxy-image?url=${encodeURIComponent(validUrl)}`;
        }

        return validUrl;
    };

    const baseImageUrl = getImageUrl();
    const imageUrl = googlePlacesImageUrl || baseImageUrl;

    // Google Places APIから画像を取得を試行
    useEffect(() => {
        if (!baseImageUrl && shouldTryGooglePlacesImage(spot) && !isLoadingGoogleImage && !googlePlacesImageUrl) {
            setIsLoadingGoogleImage(true);

            // Google Places APIを呼び出し（簡易実装）
            const searchParams = new URLSearchParams({
                name: spot.name,
                lat: spot.location.lat.toString(),
                lng: spot.location.lng.toString(),
                radius: '100'
            });

            fetch(`https://flaato-api.flaato-app.workers.dev/api/places/search?${searchParams}`)
                .then(response => response.json())
                .then((data: any) => {
                    if (data?.place?.photos && data.place.photos.length > 0) {
                        const photoName = data.place.photos[0].name;
                        const photoUrl = `https://flaato-api.flaato-app.workers.dev/api/places/photo?photoName=${encodeURIComponent(photoName)}&maxWidthPx=400&maxHeightPx=300`;
                        setGooglePlacesImageUrl(photoUrl);
                        console.log('✅ Google Places image found for:', spot.name);
                    }
                })
                .catch(error => {
                    console.log('❌ Google Places image search failed for:', spot.name, error);
                })
                .finally(() => {
                    setIsLoadingGoogleImage(false);
                });
        }
    }, [spot.sourceId, baseImageUrl]); // sourceIdのみに依存し、isLoadingGoogleImageを除外

    console.log('🔍 SpotImageMinimal:', {
        spotName: spot.name,
        imageUrl: imageUrl || 'NO IMAGE',
        hasImage: !!imageUrl
    });

    const handleClick = () => {
        const mapsUrl = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(spot.name)}`;
        window.open(mapsUrl, '_blank', 'noopener,noreferrer');
    };

    return (
        <div
            className={`${className} relative overflow-hidden cursor-pointer group`}
            onClick={handleClick}
            role="button"
            tabIndex={0}
            aria-label={`${spot.name}をGoogleマップで開く`}
        >
            {imageUrl ? (
                <img
                    src={imageUrl}
                    alt={spot.name}
                    className="w-full h-full object-cover rounded-md"
                    onLoad={() => console.log('✅ Minimal image loaded:', spot.name)}
                    onError={() => console.log('❌ Minimal image error:', spot.name)}
                />
            ) : (
                <div className="w-full h-full bg-gradient-to-br from-blue-50 to-blue-100 flex items-center justify-center text-blue-600 rounded-md">
                    <div className="text-center">
                        <div className="text-2xl mb-1">📍</div>
                        <div className="text-xs">{spot.categoryJa || spot.genre}</div>
                        {isLoadingGoogleImage && (
                            <div className="text-xs mt-1 opacity-70">画像検索中...</div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default SpotImageMinimal;